var _excluded = ["tabs", "activeTab", "onChange", "variant", "data-testid"],
    _excluded2 = ["name"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { forwardRef, useTestId } from '../../../utils';
import { useMultiStyleConfig } from '../../../utils/use-style-config';
import { Badge, Counter } from '../../dataDisplay';
import { Divider } from '../../dataDisplay/Divider';
import { Box } from '../../internal';
import { useScrollToActiveTab, useTabs } from './hooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var Tabs = forwardRef((_ref, forwardRef) => {
  var {
    tabs,
    activeTab,
    onChange,
    variant,
    'data-testid': dataTestId = 'tabs'
  } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);

  var styles = useMultiStyleConfig('Tabs', {
    variant
  });
  var getTestId = useTestId(dataTestId);
  var tabsListRef = useRef(null);
  useImperativeHandle(forwardRef, () => tabsListRef.current);
  var getTabCounters = useCallback(tab => {
    if (!tab.counters) return; // renders one badge for all tabs, and only for last tab renders maximum 2 badges.

    return tab.counters.map((counter, i) => i <= 1 && /*#__PURE__*/_jsx(Counter, _objectSpread({}, counter), "tab-".concat(tab.name, "-counter-").concat(i)));
  }, []);
  var getTabBadges = useCallback((tab, index) => {
    if (!tab.badges) return;
    var isLastTab = index === tabs.length - 1; // renders one badge for all tabs, and only for last tab renders maximum 2 badges.

    return tab.badges.map((badge, i) => (i === 0 || isLastTab && i === 1) && /*#__PURE__*/_jsx(Badge, _objectSpread({}, badge), "tab-".concat(tab.name, "-badge-").concat(i)));
  }, [tabs]);
  var activeTabIndex = useMemo(() => tabs.findIndex(tab => tab.name === activeTab), [tabs, activeTab]);
  var {
    tabsRefs
  } = useScrollToActiveTab({
    tabsListRef,
    tabs,
    activeTabIndex
  });
  var {
    handleKeyDown
  } = useTabs({
    tabs,
    activeTabIndex,
    onChange,
    tabsRefs
  });
  return /*#__PURE__*/_jsxs(Box, _objectSpread(_objectSpread(_objectSpread({
    "data-component": "Tabs",
    __css: styles['container']
  }, props), getTestId()), {}, {
    children: [/*#__PURE__*/_jsx(Box, _objectSpread(_objectSpread({
      __css: styles['tabs'],
      role: "tablist",
      ref: tabsListRef
    }, getTestId('list')), {}, {
      children: tabs.map((tab, index) => {
        var {
          name
        } = tab,
            rest = _objectWithoutProperties(tab, _excluded2);

        return /*#__PURE__*/_jsxs(Box, _objectSpread(_objectSpread(_objectSpread({
          role: "tab",
          as: "button",
          tabIndex: activeTab === name ? 0 : -1,
          __css: styles['tab']
        }, getTestId("tab-".concat(name))), {}, {
          "aria-selected": activeTab === name,
          "aria-label": tab['aria-label'],
          onClick: () => {
            onChange(name);
          },
          onKeyDown: handleKeyDown
        }, rest), {}, {
          ref: tabsRefs[index],
          children: [/*#__PURE__*/_jsx(Box, {
            as: "span",
            id: "tab-".concat(name),
            children: tab.label
          }), tab.counters && /*#__PURE__*/_jsx(Box, _objectSpread(_objectSpread({
            sx: styles['counters']
          }, getTestId('tab-counters')), {}, {
            children: getTabCounters(tab)
          })), tab.badges && /*#__PURE__*/_jsx(Box, _objectSpread(_objectSpread({
            sx: styles['badges']
          }, getTestId('tab-badges')), {}, {
            children: getTabBadges(tab, index)
          }))]
        }), "tab-".concat(name));
      })
    })), /*#__PURE__*/_jsx(Divider, {})]
  }));
});
Tabs.displayName = 'Tabs';
//# sourceMappingURL=Tabs.js.map